/**
 * Timeline Project Bar Component
 * Shows projects in a timeline format
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Link } from 'wouter';
import { User } from 'lucide-react';
import DualCompanyLogo from '@/components/ui/dual-company-logo';
import { companies, activities } from '../../../../db';
import { getCompanyDisplayInfo } from '@/lib/companyDisplayUtils';
import { createCompanySlug } from '@/lib/companyUtils';
import { TimelineProjectBarProps } from './types';

export const TimelineProjectBar: React.FC<TimelineProjectBarProps> = ({ 
  project, 
  position, 
  yPosition, 
  showCompanyLogos, 
  index 
}) => {
  
  const isPrivateProject = project.currentActivityId === null;
  const activity = project.currentActivityId ? activities.find(a => a.id === project.currentActivityId) : null;
  const displayInfo = activity ? getCompanyDisplayInfo(activity, companies) : null;
  const company = companies.find(c => c.name === project.currentCompanyName);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1 }}
      className="absolute group cursor-pointer"
      style={{
        left: position.left,
        width: position.width,
        top: yPosition,
        height: '48px',
        zIndex: 80
      }}
    >
      <Link href={`/project/${project.title.toLowerCase().replace(/ /g, '-')}`}>
        <div className="relative h-full">
          {/* Project Bar */}
          <div className="w-full h-full rounded-lg cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.01] relative overflow-hidden bg-gradient-to-r from-primary/60 to-primary/70 border border-primary/70">
            <div className="flex items-center h-full px-3 text-white">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {showCompanyLogos && !isPrivateProject && displayInfo?.isDualCompany ? (
                  <DualCompanyLogo
                    assignmentCompany={displayInfo.assignmentCompany}
                    tempAgency={displayInfo.tempAgency!}
                    size="sm"
                    className="flex-shrink-0"
                    onAssignmentCompanyClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.location.href = `/professional/${createCompanySlug(displayInfo.assignmentCompany.name)}`;
                    }}
                    onTempAgencyClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.location.href = `/professional/${createCompanySlug(displayInfo.tempAgency!.name)}`;
                    }}
                  />
                ) : showCompanyLogos && company ? (
                  <div 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.location.href = `/professional/${createCompanySlug(company.name)}`;
                    }}
                    className="cursor-pointer hover:scale-110 transition-transform duration-200"
                    title={`View ${company.name} details`}
                  >
                    <Avatar className="w-6 h-6 flex-shrink-0">
                      <AvatarImage src={company.logoPath || undefined} />
                      <AvatarFallback className="text-xs bg-white text-primary">
                        {company.logoText}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                ) : showCompanyLogos && isPrivateProject ? (
                  <User className="w-4 h-4 flex-shrink-0" />
                ) : null}
                
                <span className="text-sm font-medium truncate">
                  {project.title}
                </span>
                
                {(project.isMultiActivity || project.isContinued) && (
                  <Badge variant="outline" className="bg-white/20 text-white border-white/30 text-xs">
                    {project.isMultiActivity ? 'Multi' : 'Cont.'}
                  </Badge>
                )}
              </div>
              
              {(!project.endDate || project.endDate === 'Present') && (
                <div className="flex items-center gap-1 text-white/80 text-xs flex-shrink-0">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-sm shadow-green-500/50"></div>
                </div>
              )}
            </div>
          </div>
          
          {/* Tooltip */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
            <div className="font-medium">{project.title}</div>
            <div className="text-xs text-gray-300">
              {new Date(project.startDate).toLocaleDateString()} - {
                project.endDate && project.endDate !== 'Present' 
                  ? new Date(project.endDate).toLocaleDateString()
                  : 'Present'
              }
            </div>
            <div className="text-xs text-gray-300">{project.currentCompanyName}</div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};